import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { projects } from '../mock';
import { Button } from '../components/ui/button';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find(p => p.id === parseInt(id));

  if (!project) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-charcoal mb-4">Project not found</h2>
          <Link to="/projects">
            <Button>Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/projects">
          <Button variant="ghost" className="mb-8 text-charcoal hover:text-copper">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Projects
          </Button>
        </Link>

        <div className="space-y-8">
          {/* Project Header */}
          <div>
            <span className="text-sm uppercase tracking-wider text-burgundy font-semibold">
              {project.category}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mt-2 mb-4">
              {project.title}
            </h1>
            <div className="flex flex-wrap gap-4 text-neutral-600">
              <span>{project.year}</span>
              <span>•</span>
              <span>{project.medium}</span>
              {project.duration && (
                <>
                  <span>•</span>
                  <span>{project.duration}</span>
                </>
              )}
              {project.dimensions && (
                <>
                  <span>•</span>
                  <span>{project.dimensions}</span>
                </>
              )}
            </div>
          </div>

          {/* Video Embed or Image */}
          {project.videoUrl ? (
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-charcoal">
              <iframe
                src={project.videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={project.title}
              />
            </div>
          ) : (
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-off-white">
              <img
                src={project.thumbnail}
                alt={project.title}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Project Description */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-neutral-700 leading-relaxed">
              {project.description}
            </p>
          </div>

          {/* Project Details */}
          <div className="border-t border-neutral-300 pt-8">
            <h3 className="text-xl font-semibold text-charcoal mb-4">Details</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-neutral-500 mb-1">Year</dt>
                <dd className="text-neutral-900">{project.year}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 mb-1">Medium</dt>
                <dd className="text-neutral-900">{project.medium}</dd>
              </div>
              {project.materials && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-neutral-500 mb-1">Materials</dt>
                  <dd className="text-neutral-900">{project.materials}</dd>
                </div>
              )}
              {project.dimensions && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500 mb-1">Dimensions</dt>
                  <dd className="text-neutral-900">{project.dimensions}</dd>
                </div>
              )}
              {project.format && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500 mb-1">Format</dt>
                  <dd className="text-neutral-900">{project.format}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;